import { DestroyRef, Injectable, effect, inject, signal } from '@angular/core';
import { AuthStore } from '@huddle-sports-platform/app-core';
import { applyClubTheme, clearClubTheme } from '@huddle-sports-platform/ui';
import { doc, onSnapshot } from 'firebase/firestore';

import { applyClubChrome, restoreHuddleChrome } from '../core/club-chrome';
import { clubSlugFromLocation } from '../core/club-slug';
import { awaitCrest } from '../core/crest';
import { db } from '../core/firebase';
import { WHITE_LABEL, type WhiteLabelConfig } from '../core/whitelabel';
import { lookupClubBySlug } from './callables';

/**
 * A CACHE of the last resolved brand, keyed by its slug - never the answer
 * to "which club is this". The URL is that (see core/club-slug); this only
 * saves a reload from repainting through the unbranded theme while the
 * lookup is in flight, and a stale entry for another club is ignored.
 */
const STORAGE_KEY = 'huddle-club-brand';

/**
 * The one source of locked-club truth, and it has exactly two inputs.
 *
 * - **Native**: the brand is BAKED (`WHITE_LABEL`) by the flavour pipeline.
 * - **Web**: the brand is the SLUG IN THE URL - `/dublinraiders/sign-in` is
 *   the Raiders' app, in any tab, from any link, on any day. Resolving it
 *   through `lookupClubBySlug` is this store's other job.
 *
 * Either way, constructing this store applies the club's theme - it is
 * injected by the root component, so the override exists before the first
 * routed page paints, and the boot veil holds on {@link whenReady} until
 * the lookup lands (see app.ts).
 *
 * A slug that resolves to nothing is a wrong address, not a Huddle visit:
 * the page is sent to `/`, where the shared app lives, rather than leaving
 * a club's URL in the bar showing somebody else's door.
 */
@Injectable({ providedIn: 'root' })
export class ClubBrandStore {
  private readonly _brand = signal<WhiteLabelConfig | null>(null);
  /** The active club brand - null = the shared Huddle app. */
  readonly brand = this._brand.asReadonly();

  private readonly _settled = signal(false);
  /** True once the brand is decided - synchronously for a baked flavour and
   *  for the shared app, after the lookup for a club URL. The boot veil
   *  holds on it, so no door paints through the wrong theme (see app.ts). */
  readonly settled = this._settled.asReadonly();

  /** The resolved club id, tracked apart from the full brand so the live
   *  listener re-arms only on auth or club change - not on every field a
   *  snapshot updates. */
  private readonly _clubId = signal<string | null>(null);

  private readonly auth = inject(AuthStore);
  /** The live club-doc registration and the id it watches. */
  private clubUnsub: (() => void) | null = null;
  private watchingClubId: string | null = null;

  constructor() {
    // Once an admin is signed in and the club is known, keep the brand's
    // name/location/logo/colours LIVE off the club doc (SDK onSnapshot, not
    // a Callable) - an HQ edit reflects without a reload. Auth-gated because
    // reading the club doc needs the signed-in member; best-effort, so a
    // member without read access simply keeps the first-paint brand.
    effect(() => {
      const uid = this.auth.user()?.uid ?? null;
      const clubId = this._clubId();
      if (uid && clubId) this.watchClub(clubId);
      else this.stopWatch();
    });
    inject(DestroyRef).onDestroy(() => this.stopWatch());

    const baked = WHITE_LABEL;
    if (baked) {
      this.set(baked);
      this._settled.set(true);
      return;
    }
    const slug = clubSlugFromLocation();
    if (!slug) {
      // The shared Huddle app. Any cached club is another URL's business.
      this.forgetCache();
      this._settled.set(true);
      return;
    }
    // Paint the club immediately when the cache already holds THIS slug -
    // the lookup then confirms it (a renamed club or a new crest lands on
    // the next load, which is soon enough for a shop window).
    const cached = this.readCache(slug);
    if (cached) this.set(cached);
    void this.resolve(slug, !cached);
  }

  /**
   * Adopt a club this page is not itself addressed to.
   *
   * One caller: `/auth/action`. Firebase pins a project to a SINGLE action
   * URL, so that page cannot carry a club in its own address and reads the
   * one that asked out of the link's `continueUrl` instead. Silent on a
   * miss - an unrecognised club there is a reason to stay Huddle-branded,
   * never to move anybody anywhere.
   */
  async adopt(slug: string): Promise<void> {
    if (WHITE_LABEL || this.brand()?.slug === slug) return;
    try {
      const result = await lookupClubBySlug({ slug });
      if (!result.found || !result.club) return;
      const c = result.club;
      this.set({
        clubId: c.clubId,
        slug: c.slug,
        appName: c.appName,
        sport: c.sport,
        logoUrl: c.logoUrl,
        primaryColour: c.primaryColour,
        secondaryColour: c.secondaryColour,
        theme: c.theme,
      });
    } catch {
      // Unbranded is a perfectly good way to reset a password.
    }
  }

  /** Ask the backend whose app this URL is. */
  private async resolve(slug: string, strict: boolean): Promise<void> {
    try {
      const result = await lookupClubBySlug({ slug });
      if (result.found && result.club) {
        const c = result.club;
        this.set({
          clubId: c.clubId,
          slug: c.slug,
          appName: c.appName,
          sport: c.sport,
          logoUrl: c.logoUrl,
          primaryColour: c.primaryColour,
          secondaryColour: c.secondaryColour,
          theme: c.theme,
        });
        return;
      }
      // A definitive no: no club answers to this address. Hand the visit to
      // the shared app AT THE ROOT - leaving a club's URL in the bar over
      // somebody else's door is the confusion this whole design removes.
      this.clear();
      window.location.replace('/');
    } catch {
      // Transport trouble is NOT a verdict on the club - a member on a
      // flaky connection must not be thrown out of their own app. Keep a
      // cached brand if we had one; otherwise carry on unbranded.
      if (strict) this.clear();
    } finally {
      // Either way the question is answered and the veil can lift; a
      // redirect that is already under way simply outlives the frame.
      this._settled.set(true);
    }
  }

  /** The cached brand for this slug, or null - a stale club is not it. */
  private readCache(slug: string): WhiteLabelConfig | null {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      const config = JSON.parse(stored) as WhiteLabelConfig;
      return config.slug === slug ? config : null;
    } catch {
      return null;
    }
  }

  private forgetCache(): void {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to remove */
    }
  }

  /**
   * Attach a realtime listener to the club doc, merging its live fields
   * (name, location, logo, crest colours, sport) into the brand. The
   * wordmark and the derived accent theme stay from the first paint - those
   * live in the white-label manifest, not the club doc. Idempotent:
   * re-arming the same club is a no-op.
   */
  private watchClub(clubId: string): void {
    if (this.watchingClubId === clubId) return;
    this.stopWatch();
    this.watchingClubId = clubId;
    this.clubUnsub = onSnapshot(
      doc(db(), 'clubs', clubId),
      (snap) => {
        const data = snap.data();
        const current = this._brand();
        if (!data || !current || current.clubId !== clubId) return;
        this.set({
          ...current,
          name: data['name'] as string | undefined,
          location: data['location'] as string | undefined,
          logoUrl: (data['logoUrl'] as string | null | undefined) ?? current.logoUrl,
          primaryColour: (data['primaryColour'] as string | undefined) ?? current.primaryColour,
          secondaryColour:
            (data['secondaryColour'] as string | null | undefined) ?? current.secondaryColour,
          sport: (data['sport'] as string | undefined) ?? current.sport,
        });
      },
      () => {
        // Permission/transport trouble is not a verdict - keep the
        // first-paint brand rather than blanking a live app.
      },
    );
  }

  private stopWatch(): void {
    this.clubUnsub?.();
    this.clubUnsub = null;
    this.watchingClubId = null;
  }

  /** Adopt a brand: theme on, cached for the next load of the same slug. */
  set(config: WhiteLabelConfig): void {
    this._brand.set(config);
    this._clubId.set(config.clubId);
    applyClubTheme(config.theme);
    applyClubChrome(config);
    // Hold the boot gate until this club's crest can paint (main.ts has
    // already started the same wait on native; asking twice is free).
    void awaitCrest(config.logoUrl);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(config));
    } catch {
      // Storage full/blocked - the theme still applies for this load.
    }
  }

  /** Back to Huddle (an unknown slug, or leaving a club's door). */
  clear(): void {
    this._brand.set(null);
    this._clubId.set(null);
    clearClubTheme();
    restoreHuddleChrome();
    void awaitCrest(null);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* nothing to remove */
    }
  }
}
